import * as React from "react";
import { useEffect, useRef, useState } from "react";
import ohlogo from "/src/assets/olympic-holidays-logo.svg";

// Site footer, fed by /api/footer — the CMS home page's footer content (link columns, "Book with
// us", trust logos, small-print links) via Jetstream, cached server-side. The same footer as the
// website, on every page except login (siteWrapper).

// One fetch per page load however many times the footer mounts; failures just hide the CMS parts.
let footerRequest = null;
const loadFooter = () => {
    footerRequest ??= fetch("/api/footer", { credentials: "same-origin" })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    return footerRequest;
};

const SOCIAL = [
    { name: "Facebook", href: "https://www.facebook.com/olympicholidays",
      path: "M13.5 22v-8h2.7l.4-3.2h-3.1V8.7c0-.9.3-1.6 1.7-1.6h1.6V4.2c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4.1v2.6H7.6V14h2.8v8h3.1z" },
    { name: "TikTok", href: "https://www.tiktok.com/@olympicholidays",
      path: "M16.6 5.8A4.3 4.3 0 0 1 15.5 3h-3.1v12.4a2.6 2.6 0 1 1-2.6-2.6c.3 0 .5 0 .8.1V9.7a5.7 5.7 0 1 0 4.9 5.7V9.2a7.4 7.4 0 0 0 4.3 1.4V7.5c-1.3 0-2.4-.7-3.2-1.7z" },
    { name: "Instagram", href: "https://www.instagram.com/olympicholidays/", stroke: true,
      path: "M8 3h8a5 5 0 0 1 5 5v8a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5V8a5 5 0 0 1 5-5zm4 5a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm5.2-1.2h.01" },
    { name: "YouTube", href: "https://www.youtube.com/user/holidays00",
      path: "M23 8s-.2-1.6-.9-2.3c-.9-.9-1.9-.9-2.3-1C16.6 4.5 12 4.5 12 4.5s-4.6 0-7.8.2c-.4.1-1.4.1-2.3 1C1.2 6.4 1 8 1 8S.8 9.9.8 11.8v1.7C.8 15.4 1 17.3 1 17.3s.2 1.6.9 2.3c.9.9 2 .9 2.5 1 1.8.2 7.6.2 7.6.2s4.6 0 7.8-.2c.4-.1 1.4-.1 2.3-1 .7-.7.9-2.3.9-2.3s.2-1.9.2-3.8v-1.7C23.2 9.9 23 8 23 8zM9.8 15.3V8.7l6.2 3.3-6.2 3.3z" },
];

const linkProps = (link) => ({
    href: link.url,
    target: link.target || undefined,
    rel: link.target === "_blank" ? "noopener" : undefined,
});

const NavLinks = ({ links }) => (
    <ul className="space-y-1.5">
        {links.map((link, i) => (
            <li key={i}>
                <a {...linkProps(link)} className="text-sm text-white/85 hover:text-white hover:underline">{link.name}</a>
            </li>
        ))}
    </ul>
);

// "Book with us": white panel so the CMS content (the "Book with confidence" image and the light
// Trustpilot TrustBox) reads correctly against the navy footer.
// The CMS markup is memoised on its string: a re-render that re-applied innerHTML would throw away
// the TrustBox iframe Trustpilot injected into it. (CMS-authored HTML, rendered raw exactly as the
// website does.)
const CmsHtml = React.memo(({ html }) => (
    <div className="text-sm [&_p:empty]:hidden [&_p]:mb-2 [&_a]:underline" dangerouslySetInnerHTML={{ __html: html }} />
));

const SectionPanel = ({ section }) => (
    <div className="bg-white text-primary rounded-[14px] p-4">
        {section.iconUrl && <img src={section.iconUrl} alt={section.title || ""} className="w-full max-w-[260px] h-auto rounded-lg mb-3" loading="lazy" />}
        {section.html && <CmsHtml html={section.html} />}
    </div>
);

export default function Footer() {
    const [footer, setFooter] = useState(null);
    const footerRef = useRef(null);

    useEffect(() => { let live = true; loadFooter().then((f) => live && setFooter(f)); return () => { live = false; }; }, []);

    // Trustpilot's TrustBox (inside the CMS "Book with us" markup) needs its bootstrap script and an
    // explicit init once the markup is in the DOM. Both the phone and desktop copies get initialised;
    // whichever the breakpoint hides costs nothing.
    useEffect(() => {
        if (!footer?.section?.html?.includes("trustpilot-widget")) return;
        if (!document.getElementById("trustpilot-widget-script")) {
            const script = document.createElement("script");
            script.id = "trustpilot-widget-script";
            script.src = "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
            script.async = true;
            document.head.appendChild(script);
        }
        // Wait (up to ~10s) for the bootstrap, then initialise each TrustBox exactly once — calling
        // loadFromElement again on the same element restarts it, so the iframe never settles. If the
        // script never arrives the CMS fallback link stays.
        let tries = 0;
        const timer = setInterval(() => {
            if (!window.Trustpilot?.loadFromElement) { if (++tries >= 20) clearInterval(timer); return; }
            clearInterval(timer);
            // Widgets the bootstrap already rendered on load keep their iframe; only bare ones are initialised.
            footerRef.current?.querySelectorAll(".trustpilot-widget:not([data-tp-init])").forEach((el) => {
                el.setAttribute("data-tp-init", "1");
                if (!el.querySelector("iframe")) window.Trustpilot.loadFromElement(el, true);
            });
        }, 500);
        return () => clearInterval(timer);
    }, [footer]);

    const sections = footer?.navSections || [];
    const section = footer?.section;
    const logos = footer?.logos || [];
    const infoLinks = footer?.siteInfoLinks || [];
    const legal = footer?.legalText || "Package holidays are provided by Olympic Holidays (trading name of Travelworld Vacations Ltd) ATOL 4108. Olympic is a registered trademark.";
    const hasColumns = sections.length > 0 || !!section;

    return (
        <footer ref={footerRef} className="mt-2 rounded-card bg-emp_deep text-white px-5 py-6 md:px-9 md:py-8" role="contentinfo">
            <div className="flex flex-col items-center gap-4 md:flex-row md:items-center md:justify-between">
                <a href={window.wwwUrl} className="inline-flex" aria-label="Olympic Holidays">
                    <img src={ohlogo} alt="Olympic Holidays" className="h-11 w-auto brightness-0 invert" />
                </a>
                <div className="flex gap-2.5">
                    {SOCIAL.map((s) => (
                        <a key={s.name} href={s.href} target="_blank" rel="noopener" aria-label={s.name}
                           className="grid h-10 w-10 place-items-center rounded-full bg-white text-emp_deep hover:bg-emp_soft transition">
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill={s.stroke ? "none" : "currentColor"}
                                 stroke={s.stroke ? "currentColor" : "none"} strokeWidth={s.stroke ? 2 : 0}
                                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d={s.path} />
                            </svg>
                        </a>
                    ))}
                </div>
            </div>

            {hasColumns && (
                <>
                    {/* Phones: accordions. */}
                    <div className="md:hidden mt-6 border-t border-white/15">
                        {sections.map((s, i) => (
                            <details key={i} className="border-b border-white/15 group">
                                <summary className="flex items-center justify-between py-3.5 text-[15px] font-semibold cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                                    {s.title}
                                    <span className="text-2xl leading-none font-light group-open:hidden">+</span>
                                    <span className="text-2xl leading-none font-light hidden group-open:inline">−</span>
                                </summary>
                                <div className="pb-4"><NavLinks links={s.links} /></div>
                            </details>
                        ))}
                        {section && (
                            <div className="py-4">
                                {section.title && <p className="text-[15px] font-semibold mb-3">{section.title}</p>}
                                <SectionPanel section={section} />
                            </div>
                        )}
                    </div>
                    {/* Desktop: columns, the "Book with us" panel on the right. */}
                    <div className="hidden md:grid mt-8 gap-8 lg:gap-10 md:grid-cols-[1fr_1.4fr_1fr]">
                        {sections.map((s, i) => (
                            <div key={i} className={sections.length === 1 ? "md:col-span-2" : ""}>
                                <p className="font-poppins font-semibold text-[15px] mb-3 pb-2 border-b border-white/15">{s.title}</p>
                                {s.links.length > 6 ? (
                                    <div className="grid grid-cols-2 gap-x-6">
                                        <NavLinks links={s.links.slice(0, Math.ceil(s.links.length / 2))} />
                                        <NavLinks links={s.links.slice(Math.ceil(s.links.length / 2))} />
                                    </div>
                                ) : <NavLinks links={s.links} />}
                            </div>
                        ))}
                        {section && (
                            <div className={sections.length === 0 ? "md:col-span-3" : ""}>
                                {section.title && <p className="font-poppins font-semibold text-[15px] mb-3 pb-2 border-b border-white/15">{section.title}</p>}
                                <SectionPanel section={section} />
                            </div>
                        )}
                    </div>
                </>
            )}

            <div className={`${hasColumns ? "mt-8 md:mt-10" : "mt-6"} border-t border-white/15 pt-6`}>
                {logos.length > 0 && (
                    <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 md:justify-start">
                        {logos.map((logo, i) => {
                            const img = <img src={logo.imageUrl} alt={logo.altText || ""} className="h-9 md:h-10 w-auto rounded-sm bg-white/95 p-0.5" loading="lazy" />;
                            return logo.url
                                ? <a key={i} href={logo.url} target={logo.target || undefined} rel={logo.target === "_blank" ? "noopener" : undefined}>{img}</a>
                                : <span key={i}>{img}</span>;
                        })}
                    </div>
                )}
                <p className="mt-5 max-w-2xl text-center text-xs text-white/70 md:text-left">{legal}</p>
                {/* Links carry explicit colours throughout: the global `a` rule would paint them navy on navy. */}
                {infoLinks.length > 0 && (
                    <p className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-white/70 md:justify-start">
                        {infoLinks.map((link, i) => (
                            <a key={i} {...linkProps(link)} className="text-white/70 hover:text-white hover:underline">{link.name}</a>
                        ))}
                    </p>
                )}
            </div>
        </footer>
    );
}
